<template>
  <v-row
    :class="[
      'py-0 px-1 py-sm-2 settings-field',
      { 'settings-section-title': field.type === 'sectionTitle' },
      { 'is-scroll-target': isScrollTarget },
    ]"
    no-gutters
    :id="slugMe(field.label)"
  >
    <SectionTitleField v-if="field.type === 'sectionTitle'" :label="label" />
    <v-col
      v-else
      cols="12"
      :sm="_includes(['gallery'], field.type) ? 12 : 8"
      :md="_includes(['gallery'], field.type) ? 12 : 6"
      class="pt-2 px-0 px-sm-3 pl-sm-0"
      order="2"
      order-sm="1"
    >
      <v-form v-model="isValid" @submit.prevent="$emit('save')">
        <ShopSettingsFieldCheckbox
          v-if="field.type === 'checkbox'"
          :label="label"
          :original="fieldVal"
          @save="save($event)"
          :hint="field.hint"
        />
        <v-text-field
          v-else-if="field.type === 'textShort'"
          ref="field"
          autocomplete="new-password"
          data-lpignore="true"
          :label="label"
          :value="fieldVal"
          @input="save($event)"
          outlined
          dense
          :rules="rules"
          :disabled="saving"
          :loading="saving"
        />
        <v-text-field
          v-else-if="field.type === 'textHidden'"
          ref="field"
          autocomplete="new-password"
          data-lpignore="true"
          :label="label"
          :value="fieldVal"
          @input="save($event)"
          outlined
          dense
          :rules="rules"
          :disabled="saving"
          :loading="saving"
          :type="revealHidden ? 'text' : 'password'"
        >
          <template #append>
            <v-icon @click="revealHidden = !revealHidden">{{
              revealHidden ? 'mdi-eye' : 'mdi-eye-off'
            }}</v-icon>
          </template>
        </v-text-field>
        <v-textarea
          v-else-if="field.type === 'textLong'"
          ref="field"
          :label="label"
          outlined
          :value="fieldVal"
          @input="save($event)"
          :rules="rules"
          :disabled="saving"
          :loading="saving"
        />
        <RichText
          v-else-if="field.type === 'textRich'"
          :content="fieldVal"
          :field-label="field.label"
          @update="save($event)"
          :is-dirty="isDirty"
          :rules="rules"
          :loading="saving"
        />
        <v-textarea
          v-else-if="field.type === 'textRich'"
          ref="field"
          :label="label"
          outlined
          :value="fieldVal"
          @input="save($event)"
          :loading="saving"
          :rules="rules"
        />
        <v-text-field
          v-else-if="field.type === 'number'"
          ref="field"
          :label="label"
          outlined
          dense
          type="number"
          :value="fieldVal"
          @input="save($event)"
          :rules="rules"
          :loading="saving"
        />
        <v-text-field
          v-else-if="field.type === 'price'"
          ref="field"
          :label="label"
          outlined
          dense
          type="number"
          prefix="$"
          :value="Number(fieldVal) / 100"
          @input="save(Number($event) * 100)"
          :rules="rules"
          :loading="saving"
        />
        <v-select
          v-else-if="field.type === 'select'"
          ref="field"
          :label="label"
          outlined
          type="number"
          :value="fieldVal"
          :items="field.options"
          @input="save($event)"
          :rules="rules"
          :loading="saving"
          clearable
        />
        <ShopSettingsFieldMultiselect
          v-else-if="field.type === 'multiSelect'"
          ref="field"
          :label="label"
          :value="fieldVal"
          :items="field.options.map((i) => i.trim())"
          @change="saveMultiSelect"
          :loading="saving"
        />

        <v-autocomplete
          v-else-if="field.type === 'selectObject'"
          ref="field"
          :label="label"
          outlined
          :value="fieldVal"
          :items="field.options"
          item-text="name"
          item-value="value"
          @input="save($event)"
          :rules="rules"
          :loading="saving"
          clearable
        />

        <ImageField
          v-else-if="field.type === 'image'"
          @update="save($event)"
          :label="label"
          :setting-name="field.name"
          :img="fieldVal"
        />
        <GalleryField
          v-else-if="field.type === 'gallery'"
          @update="save($event)"
          :setting-name="field.name"
          :imgs="fieldVal"
        />
        <PaymentPreviewField v-else-if="field.type === 'paymentPreview'" />
        <ShopSettingsBilling
          v-else-if="field.type === 'subscriptionManagement'"
        />
        <ShopSettingsLeadTime
          v-else-if="field.type === 'leadTime'"
          @update="save($event)"
          :label="field.label"
          :value="fieldVal"
        />
        <ShopSettingsCancelSubscription
          v-else-if="field.type === 'cancelSubscription'"
        />
        <v-text-field
          v-else
          autocomplete="new-password"
          data-lpignore="true"
          ref="field"
          outlined
          :label="label"
          dense
          :value="fieldVal"
          @input="save($event)"
          :rules="rules"
          :loading="saving"
        />
      </v-form>
    </v-col>
    <v-col
      v-if="isShowingCustomHint"
      cols="12"
      :sm="_includes(['gallery'], field.type) ? 12 : 4"
      :class="['hint text-body-1', { border: !_includes(['xs', 'sm'], $mq) }]"
      order="1"
      order-sm="2"
    >
      {{ field.hint }}
    </v-col>
    <v-col cols="12" v-if="$mq === 'xs' && field.type !== 'sectionTitle'">
      <v-divider class="my-2" />
    </v-col>
  </v-row>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import SanitizeHtml from 'sanitize-html'
import debounce from 'debounce'
import slugMe from '@/utils/slugMe'
import ValidationRules from '@/constants/ValidationRules'

import _isEqual from 'lodash/isEqual'
import _includes from 'lodash/includes'

import RichText from '@/components/RichText'
import GalleryField from '@/components/GalleryField'
import ImageField from '@/components/ImageField'
import ShopSettingsFieldMultiselect from '@/components/ShopSettingsFieldMultiselect'
import ShopSettingsFieldCheckbox from '@/components/ShopSettingsFieldCheckbox'
import PaymentPreviewField from '@/components/PaymentPreviewField'
import SectionTitleField from '@/components/SectionTitleField'
import ShopSettingsBilling from '@/components/ShopSettingsBilling'
import ShopSettingsCancelSubscription from '@/components/ShopSettingsCancelSubscription'
import ShopSettingsLeadTime from '@/components/ShopSettingsLeadTime'

export default {
  name: 'ShopSettingsField',
  props: {
    field: {
      type: Object,
      required: true,
      default: () => {},
    },
    isScrollTarget: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    RichText,
    GalleryField,
    ImageField,
    PaymentPreviewField,
    ShopSettingsFieldMultiselect,
    ShopSettingsFieldCheckbox,
    SectionTitleField,
    ShopSettingsBilling,
    ShopSettingsCancelSubscription,
    ShopSettingsLeadTime,
  },
  computed: {
    ...mapGetters('shop', [
      'getShopDetail',
      'getBooleanToggle',
      'getShopMultiSelectDetail',
    ]),
    rules() {
      let rules = this.field.rules
        ? this.field.rules
            .filter((ruleName) => ValidationRules[ruleName])
            .map((ruleName) =>
              ValidationRules[ruleName] ? ValidationRules[ruleName] : () => true
            )
        : []
      return rules
    },
    isShowingCustomHint() {
      if (this.field.type === 'checkbox') {
        return !this.field.hint && this.$mq !== 'xs' && this.$mq !== 'sm'
      }
      return this.field.hint
    },
    isDirty() {
      let { stagedChanges, original } = this
      let isDirty = false
      if (Array.isArray(stagedChanges) && Array.isArray(original)) {
        isDirty = !_isEqual(stagedChanges, original)
      } else {
        isDirty =
          typeof stagedChanges !== 'undefined' && stagedChanges !== original
      }

      return isDirty
    }, // isDirty

    label() {
      return this.field.label
    }, // label

    fieldVal() {
      let { name, type } = this.field
      let val

      switch (type) {
        case 'checkbox':
          val = this.getBooleanToggle(name)
          break
        case 'number':
        case 'leadTime':
          val = Number(this.getShopDetail(name) || 0)
          break
        case 'image':
          var image = this.getShopDetail(name)
          val = image ? JSON.parse(image) : null
          break
        case 'gallery':
          var gallery = this.getShopDetail(name)
          val = gallery ? JSON.parse(gallery) : []
          break
        case 'multiSelect':
          val = this.getShopMultiSelectDetail(name)
          break
        case 'select':
          val = (this.getShopDetail(name) || '').trim()
          break
        default:
          val = this.getShopDetail(name) || ''
          break
      }

      return val
    },
  },
  data() {
    return {
      saving: false,
      isValid: false,
      stagedChanges: undefined,
      original: undefined,
      revealHidden: false,
    }
  },
  async created() {
    this.original = await this.getShopDetail(this.field.name)
  },
  methods: {
    ...mapActions('shop', ['updateSetting']),

    save: debounce(async function(val) {
      if (this.$refs.field) {
        await this.$refs.field.validate()
      }

      if (!this.isValid) {
        return false
      }

      this.stagedChanges = val
    }, 250), // saveChanges
    saveMultiSelect(selectionsArray) {
      let updatedSelection = selectionsArray.filter(
        (selection) => !!selection.trim()
      )
      this.stagedChanges = updatedSelection
    }, // saveMultiSelect
    slugMe,
    _includes,
  }, //methods
  watch: {
    isDirty(isDirty) {
      if (isDirty) {
        this.$emit('dirtied', SanitizeHtml(this.stagedChanges))
      } else {
        this.$emit('cleaned')
      }
    },
    stagedChanges(stagedChanges) {
      this.$emit('update', SanitizeHtml(stagedChanges))
    },
    fieldVal() {
      this.original =
        this.field.type === 'multiSelect'
          ? this.getShopMultiSelectDetail(this.field.name)
          : this.getShopDetail(this.field.name)
    },
  },
}
</script>

<style lang="scss">
.settings-section-title {
  position: sticky;
  background: white;
  top: 48px;
  z-index: 2;
}
.settings-field {
  transition: background-color 0.3s ease-in-out;
  background-color: white;
  .hint {
    margin-bottom: 8px;
    white-space: pre-line;
    &.border {
      padding: 12px;
      border-left: 1px solid #e6e6e6;
    }
  }
  .v-input--checkbox {
    .v-messages__message {
      margin-top: 4px;
      margin-bottom: 4px;
      font-size: 1rem;
      line-height: 1.3;
    }
  }
  &.is-scroll-target {
    background-color: #dcfff3;
  }
}
</style>
